/**
 * Development tools for debugging and log management
 * Available globally in development mode via window.adminDevTools
 */

import logger from './logger'

class DevTools {
  constructor() {
    this.logger = logger
    this.isEnabled = process.env.NODE_ENV === 'development'
  }

  /**
   * Export all stored logs as JSON file
   */
  exportLogs() {
    if (!this.isEnabled) {
      console.warn('DevTools only available in development mode')
      return
    }
    
    logger.exportLogs()
    console.log('📁 Logs exported to downloads folder')
  }

  /**
   * Clear all stored logs
   */
  clearLogs() {
    if (!this.isEnabled) {
      console.warn('DevTools only available in development mode')
      return
    }
    
    logger.clearLogs()
    console.log('🧹 All logs cleared')
  }

  /**
   * View recent logs in console
   */
  showRecentLogs(count = 20) {
    if (!this.isEnabled) {
      console.warn('DevTools only available in development mode')
      return
    }

    const logs = JSON.parse(localStorage.getItem('admin_logs') || '[]')
    const recent = logs.slice(-count)
    
    console.group(`📋 Last ${recent.length} logs`)
    recent.forEach((log) => {
      const style = this.getLogStyle(log.level)
      console.log(
        `%c[${log.timestamp}] ${log.level}: ${log.message}`,
        style,
        log.context
      )
    })
    console.groupEnd()
  }

  /**
   * Get console styling for different log levels
   */
  getLogStyle(level) {
    const styles = {
      DEBUG: 'color: #6b7280; font-size: 11px;',
      INFO: 'color: #3b82f6; font-weight: 500;',
      WARN: 'color: #f59e0b; font-weight: 500;',
      ERROR: 'color: #ef4444; font-weight: bold;'
    }
    return styles[level] || ''
  }

  /**
   * Filter logs by criteria
   */
  filterLogs(criteria = {}) {
    if (!this.isEnabled) {
      console.warn('DevTools only available in development mode')
      return []
    }

    const logs = JSON.parse(localStorage.getItem('admin_logs') || '[]')
    let filtered = logs

    // Filter by level
    if (criteria.level) {
      filtered = filtered.filter(log => 
        log.level.toLowerCase() === criteria.level.toLowerCase()
      )
    }

    // Filter by message content
    if (criteria.message) {
      filtered = filtered.filter(log => 
        log.message.toLowerCase().includes(criteria.message.toLowerCase())
      )
    }

    // Filter by time range (last N minutes)
    if (criteria.lastMinutes) {
      const cutoff = Date.now() - (criteria.lastMinutes * 60 * 1000)
      filtered = filtered.filter(log => 
        new Date(log.timestamp).getTime() > cutoff
      )
    }

    // Filter by route
    if (criteria.route) {
      filtered = filtered.filter(log => 
        log.context && log.context.route && 
        log.context.route.includes(criteria.route)
      )
    }

    console.group(`🔍 Filtered logs (${filtered.length} results)`)
    filtered.forEach(log => {
      const style = this.getLogStyle(log.level)
      console.log(
        `%c[${log.timestamp}] ${log.level}: ${log.message}`,
        style,
        log.context
      )
    })
    console.groupEnd()

    return filtered
  }

  /**
   * Get performance metrics from logs
   */
  getPerformanceMetrics() {
    if (!this.isEnabled) {
      console.warn('DevTools only available in development mode')
      return
    }

    const logs = JSON.parse(localStorage.getItem('admin_logs') || '[]')
    const perfLogs = logs.filter(log => 
      log.context && log.context.metric
    )

    const metrics = {}
    perfLogs.forEach(log => {
      const metric = log.context.metric
      if (!metrics[metric]) {
        metrics[metric] = {
          count: 0,
          total: 0,
          min: Infinity,
          max: 0,
          values: []
        }
      }
      
      const value = log.context.value || 0
      metrics[metric].count++
      metrics[metric].total += value
      metrics[metric].min = Math.min(metrics[metric].min, value)
      metrics[metric].max = Math.max(metrics[metric].max, value)
      metrics[metric].values.push(value)
    })

    // Calculate averages
    Object.keys(metrics).forEach(metric => {
      const data = metrics[metric]
      data.average = data.total / data.count
      data.min = data.min === Infinity ? 0 : data.min
    })

    console.table(metrics)
    return metrics
  }

  /**
   * Monitor API performance
   */
  monitorAPI() {
    if (!this.isEnabled) {
      console.warn('DevTools only available in development mode')
      return
    }

    const logs = JSON.parse(localStorage.getItem('admin_logs') || '[]')
    const apiLogs = logs.filter(log => 
      log.message.includes('API Call') && log.context.duration
    )

    const apiMetrics = {}
    apiLogs.forEach(log => {
      const method = log.context.method
      const url = log.context.url
      const key = `${method} ${url}`
      
      if (!apiMetrics[key]) {
        apiMetrics[key] = {
          calls: 0,
          totalDuration: 0,
          errors: 0,
          success: 0
        }
      }
      
      apiMetrics[key].calls++
      apiMetrics[key].totalDuration += log.context.duration || 0
      
      if (log.context.response && log.context.response.status >= 400) {
        apiMetrics[key].errors++
      } else {
        apiMetrics[key].success++
      }
    })

    // Calculate averages
    Object.keys(apiMetrics).forEach(endpoint => {
      const data = apiMetrics[endpoint]
      data.avgDuration = data.totalDuration / data.calls
      data.errorRate = (data.errors / data.calls * 100).toFixed(1) + '%'
      data.successRate = (data.success / data.calls * 100).toFixed(1) + '%'
    })

    console.group('🌐 API Performance Metrics')
    console.table(apiMetrics)
    console.groupEnd()
    
    return apiMetrics
  }

  /**
   * Show help information
   */
  help() {
    console.group('🛠️ Admin DevTools Help')
    console.log('Available methods:')
    console.log('• exportLogs() - Download logs as JSON file')
    console.log('• clearLogs() - Clear all stored logs')
    console.log('• showRecentLogs(count=20) - Show recent logs in console')
    console.log('• filterLogs({level, message, lastMinutes, route}) - Filter logs')
    console.log('• getPerformanceMetrics() - Show performance metrics')
    console.log('• monitorAPI() - Show API performance metrics')
    console.log('')
    console.log('Examples:')
    console.log('• adminDevTools.filterLogs({level: "error"})')
    console.log('• adminDevTools.filterLogs({lastMinutes: 30, route: "dashboard"})')
    console.log('• adminDevTools.showRecentLogs(50)')
    console.groupEnd()
  }

  /**
   * Initialize dev tools (called automatically in development)
   */
  init() {
    if (!this.isEnabled) return

    // Make available globally
    if (typeof window !== 'undefined') {
      window.adminDevTools = this
      console.log('🛠️ Admin DevTools loaded. Type "adminDevTools.help()" for usage info.')
    }
  }
}

const devTools = new DevTools()
devTools.init()

export default devTools